import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, subscriptionsApi } from '../services/api';
import { UserDto, SubscriptionLookupResponseDto, WalletBalanceDto } from '@shared/contracts';

interface AuthContextType {
  user: UserDto | null;
  token: string | null;
  subscription: SubscriptionLookupResponseDto | null;
  walletBalance: WalletBalanceDto | null;
  isLoading: boolean;
  isSuperAdmin: boolean;
  loginWithLogto: () => Promise<void>;
  handleLogtoCallback: (code: string, state: string) => Promise<UserDto>;
  logout: () => Promise<void>;
  refreshSubscription: () => Promise<void>;
  selectActiveModel: (providerId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserDto | null>(() => {
    const saved = localStorage.getItem('sensiavera_user');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('sensiavera_token'));
  const [subscription, setSubscription] = useState<SubscriptionLookupResponseDto | null>(null);
  const [walletBalance, setWalletBalance] = useState<WalletBalanceDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchSubscriptionAndBalance = useCallback(async () => {
    try {
      const [subData, balanceData] = await Promise.all([
        subscriptionsApi.getMySubscription().catch(() => null),
        subscriptionsApi.getMyBalance().catch(() => null),
      ]);
      if (subData) setSubscription(subData);
      if (balanceData) setWalletBalance(balanceData);
    } catch (e) {
      console.warn('Subscription lookup error', e);
    }
  }, []);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const me = await authApi.getMe();
          if (me) {
            setUser(me);
            localStorage.setItem('sensiavera_user', JSON.stringify(me));
            await fetchSubscriptionAndBalance();
          }
        } catch (e) {
          // Token invalid, clear local session
          localStorage.removeItem('sensiavera_token');
          localStorage.removeItem('sensiavera_user');
          setToken(null);
          setUser(null);
        }
      }
      setIsLoading(false);
    };
    verifyToken();
  }, [token, fetchSubscriptionAndBalance]);

  const loginWithLogto = useCallback(async () => {
    setIsLoading(true);
    try {
      const redirectUri = `${window.location.origin}/callback`;
      const res = await authApi.initiateLogtoLogin(redirectUri);
      if (res?.authorizationUrl) {
        window.location.href = res.authorizationUrl;
      }
    } catch (e) {
      console.error('Failed to initiate Logto OIDC flow:', e);
      throw e;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleLogtoCallback = useCallback(async (code: string, state: string): Promise<UserDto> => {
    setIsLoading(true);
    try {
      const redirectUri = `${window.location.origin}/callback`;
      const authData = await authApi.handleLogtoCallback(code, state, redirectUri);
      const { accessToken, user: loggedInUser } = authData;

      localStorage.setItem('sensiavera_token', accessToken);
      localStorage.setItem('sensiavera_user', JSON.stringify(loggedInUser));
      setToken(accessToken);
      setUser(loggedInUser);

      await fetchSubscriptionAndBalance();
      return loggedInUser;
    } finally {
      setIsLoading(false);
    }
  }, [fetchSubscriptionAndBalance]);

  const logout = async () => {
    try {
      const res = await authApi.logout().catch(() => null);
      localStorage.removeItem('sensiavera_token');
      localStorage.removeItem('sensiavera_user');
      setToken(null);
      setUser(null);
      setSubscription(null);
      setWalletBalance(null);

      if (res?.logoutUrl) {
        window.location.href = res.logoutUrl;
      } else {
        window.location.href = '/login';
      }
    } catch (e) {
      window.location.href = '/login';
    }
  };

  const selectActiveModel = async (providerId: string) => {
    try {
      const updatedUser = await authApi.setActiveModel(providerId);
      setUser(updatedUser);
      localStorage.setItem('sensiavera_user', JSON.stringify(updatedUser));
    } catch (e) {
      console.error('Failed to select active model:', e);
      throw e;
    }
  };

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        subscription,
        walletBalance,
        isLoading,
        isSuperAdmin,
        loginWithLogto,
        handleLogtoCallback,
        logout,
        refreshSubscription: fetchSubscriptionAndBalance,
        selectActiveModel,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
